import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowDown, Network, UsersRound } from 'lucide-react'
import api, { type UserRole } from '@/lib/api'

interface OrgUser {
  id: number
  username: string
  full_name: string
  role: UserRole
  is_active: boolean
  team_id: number | null
  reports_to_id?: number | null
}

interface OrgTeam {
  id: number
  name: string
  manager_id: number | null
  manager: OrgUser | null
  members: OrgUser[]
  member_count: number
  department_name?: string | null
  division_title?: string | null
  divisions?: OrgTeam[]
}

interface OrgChartData {
  leaders: OrgUser[]
  teams: OrgTeam[]
  unassigned: OrgUser[]
}

const ROLE_DISPLAY: Record<string, string> = {
  super_admin: 'Super Admin', admin: 'Admin', director: 'Director',
  sales_manager: 'Sales Mgr', asst_sales_manager: 'Asst Sales Mgr',
  sales_executive: 'Sales Exec', chief_engineer: 'Chief Eng',
  operation_manager: 'Op. Mgr', engineer: 'Engineer', technician: 'Technician',
  finance: 'Finance', procurement: 'Procurement', storekeeper: 'Storekeeper', hr: 'HR',
}

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-violet-100 text-violet-700 border-violet-200',
  admin: 'bg-purple-100 text-purple-700 border-purple-200',
  director: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  sales_manager: 'bg-red-100 text-red-700 border-red-200',
  asst_sales_manager: 'bg-orange-100 text-orange-700 border-orange-200',
  sales_executive: 'bg-amber-100 text-amber-700 border-amber-200',
  finance: 'bg-emerald-100 text-emerald-700 border-emerald-200',
}

const AVATAR_BG: Record<string, string> = {
  super_admin: 'bg-violet-600', admin: 'bg-purple-600', director: 'bg-indigo-600',
  sales_manager: 'bg-red-700', asst_sales_manager: 'bg-orange-500',
  sales_executive: 'bg-amber-500', finance: 'bg-emerald-600',
  chief_engineer: 'bg-blue-600', operation_manager: 'bg-teal-600',
  engineer: 'bg-cyan-600', technician: 'bg-sky-600',
}

function initials(name: string): string {
  return name.split(' ').filter(Boolean).slice(0, 2).map(word => word[0].toUpperCase()).join('')
}

function PersonCard({ person, size = 'md', unresolved = false, designation }: {
  person: OrgUser; size?: 'sm' | 'md'; unresolved?: boolean; designation?: string
}) {
  const small = size === 'sm'
  const name = person.full_name || person.username
  return (
    <div className={`bg-white border rounded-xl shadow-sm flex flex-col items-center text-center ${
      unresolved ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200'
    } ${small ? 'p-3 w-36' : 'p-4 w-44'}`}>
      <div className={`${AVATAR_BG[person.role] || 'bg-slate-500'} text-white rounded-full flex items-center justify-center font-semibold shrink-0 ${small ? 'w-9 h-9 text-sm' : 'w-12 h-12 text-base'}`}>
        {initials(name)}
      </div>
      <div className={`font-medium text-slate-900 leading-tight mt-2 ${small ? 'text-xs' : 'text-sm'}`}>{name}</div>
      <span className={`mt-1.5 text-[10px] px-1.5 py-0.5 rounded-full border font-medium leading-tight ${ROLE_COLORS[person.role] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
        {ROLE_DISPLAY[person.role] || person.role}
      </span>
      {designation && <span className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{designation}</span>}
      {!person.is_active && <span className="mt-1 text-[10px] text-slate-400">Inactive</span>}
      {unresolved && <span className="mt-1 text-[10px] font-semibold text-amber-700">Needs supervisor</span>}
    </div>
  )
}

function HierarchyNode({ person, childrenBySupervisor, path }: { person: OrgUser; childrenBySupervisor: Map<number, OrgUser[]>; path: Set<number> }) {
  const children = childrenBySupervisor.get(person.id) || []
  const safeChildren = children.filter(child => !path.has(child.id))
  const nextPath = new Set(path).add(person.id)
  return (
    <div className="flex flex-col items-center shrink-0">
      <PersonCard person={person} />
      {safeChildren.length > 0 && (
        <>
          <div className="h-5 w-px bg-slate-300" />
          <div className="relative flex items-start gap-3 pt-0">
            {safeChildren.length > 1 && <div className="absolute top-0 left-[calc(50%)] -translate-x-1/2 h-px bg-slate-300" style={{ width: 'calc(100% - 11rem)' }} />}
            {safeChildren.map(child => (
              <div key={child.id} className="flex flex-col items-center">
                <div className="h-4 w-px bg-slate-300" />
                <HierarchyNode person={child} childrenBySupervisor={childrenBySupervisor} path={nextPath} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function DepartmentHierarchy({ team }: { team: OrgTeam }) {
  const members = team.manager
    ? team.members.filter(member => member.id !== team.manager?.id)
    : team.members
  // The API normally includes the head in members, but keep the root valid
  // even when a legacy response only supplies it through `manager`.
  const memberIds = new Set(team.members.map(member => member.id))
  if (team.manager) memberIds.add(team.manager.id)
  const bySupervisor = new Map<number, OrgUser[]>()
  members.forEach(member => {
    if (member.reports_to_id && memberIds.has(member.reports_to_id)) {
      const list = bySupervisor.get(member.reports_to_id) || []
      list.push(member)
      bySupervisor.set(member.reports_to_id, list)
    }
  })
  const head = team.manager && memberIds.has(team.manager.id) ? team.manager : team.manager
  const visible = new Set<number>()
  const collect = (person: OrgUser) => {
    if (visible.has(person.id)) return
    visible.add(person.id)
    ;(bySupervisor.get(person.id) || []).forEach(collect)
  }
  if (head) collect(head)
  const unresolved = members.filter(member => !visible.has(member.id))

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="font-semibold text-slate-900">{team.name}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{team.member_count} department member{team.member_count === 1 ? '' : 's'}</p>
        </div>
        <Network size={18} className="text-slate-400" />
      </div>
      <div className="overflow-x-auto p-6">
        {!head ? (
          <div className="min-w-[20rem] border border-dashed border-amber-300 bg-amber-50 rounded-xl p-5 text-sm text-amber-800">
            <strong>Department Head not assigned.</strong>
            <p className="mt-1 text-xs">Members are retained below as unresolved until a head is designated.</p>
          </div>
        ) : (
          <div className="min-w-max flex justify-center">
            <HierarchyNode person={head} childrenBySupervisor={bySupervisor} path={new Set()} />
          </div>
        )}
        {unresolved.length > 0 && (
          <div className="mt-7 border-t border-amber-200 pt-5">
            <div className="flex items-center gap-2 text-amber-800 mb-3">
              <AlertTriangle size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Unresolved reporting lines · {unresolved.length}</span>
            </div>
            <div className="flex gap-3 flex-wrap">
              {unresolved.map(person => <PersonCard key={person.id} person={person} size="sm" unresolved />)}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function VacantHead({ title }: { title: string }) {
  return (
    <div className="w-44 min-h-[8.7rem] rounded-xl border border-dashed border-amber-400 bg-amber-50/70 p-4 flex flex-col items-center justify-center text-center">
      <div className="w-10 h-10 rounded-full border border-amber-300 bg-white text-amber-700 flex items-center justify-center text-lg font-light">—</div>
      <strong className="mt-2 text-xs text-amber-900">Head not assigned</strong>
      <span className="mt-1 text-[10px] leading-snug text-amber-700">{title}</span>
    </div>
  )
}

function EngineeringHierarchy({ team }: { team: OrgTeam }) {
  const divisions = team.divisions || []
  const ce = team.manager
  const outsideDivisions = team.members.filter(person => person.id !== ce?.id)

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-blue-700">Engineering Department</p>
          <h2 className="mt-1 font-semibold text-slate-900">One department. Three managed divisions.</h2>
          <p className="mt-1 text-xs text-slate-500">Each division reports to the Chief Engineer through its designated head.</p>
        </div>
        <Network size={18} className="text-blue-700 shrink-0 mt-1" />
      </div>
      <div className="overflow-x-auto p-5 sm:p-7">
        <div className="min-w-max flex flex-col items-center">
          {ce ? (
            <PersonCard person={ce} designation="Chief Engineer · Department Head" />
          ) : (
            <VacantHead title="Chief Engineer · Department Head" />
          )}
          <div className="h-7 w-px bg-slate-300" />
          <div className="relative flex items-start gap-5 sm:gap-8 pt-0">
            {divisions.length > 1 && (
              <div className="absolute top-0 left-[calc(50%)] -translate-x-1/2 h-px bg-slate-300"
                style={{ width: `calc(100% - ${176 + (divisions.length - 1) * 32}px)` }} />
            )}
            {divisions.map(division => {
              const head = division.manager
              const members = head
                ? division.members.filter(person => person.id !== head.id)
                : division.members
              const memberIds = new Set(division.members.map(person => person.id))
              if (head) memberIds.add(head.id)
              const bySupervisor = new Map<number, OrgUser[]>()
              members.forEach(person => {
                if (person.reports_to_id && memberIds.has(person.reports_to_id)) {
                  const list = bySupervisor.get(person.reports_to_id) || []
                  list.push(person)
                  bySupervisor.set(person.reports_to_id, list)
                }
              })
              const visible = new Set<number>()
              const collect = (person: OrgUser) => {
                if (visible.has(person.id)) return
                visible.add(person.id)
                ;(bySupervisor.get(person.id) || []).forEach(collect)
              }
              if (head) collect(head)
              const unresolved = division.members.filter(person => !visible.has(person.id))
              const title = division.division_title || division.name
              return (
                <div key={division.id} className="relative flex flex-col items-center">
                  <div className="h-4 w-px bg-slate-300" />
                  {head
                    ? <PersonCard person={head} designation={title} />
                    : <VacantHead title={title} />}
                  {head && (members.length > 0) && (
                    <>
                      <div className="h-5 w-px bg-slate-300" />
                      <div className="flex items-start gap-3">
                        {(bySupervisor.get(head.id) || []).map(child => (
                          <div key={child.id} className="flex flex-col items-center">
                            <HierarchyNode person={child} childrenBySupervisor={bySupervisor} path={new Set([head.id])} />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {unresolved.length > 0 && (
                    <div className="mt-4 w-full max-w-[22rem] border-t border-amber-200 pt-3">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 mb-2">
                        <AlertTriangle size={13} /> Unresolved reporting lines · {unresolved.length}
                      </div>
                      <div className="flex flex-wrap justify-center gap-2">
                        {unresolved.map(person => <PersonCard key={person.id} person={person} size="sm" unresolved />)}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {divisions.length === 0 && (
            <p className="mt-5 text-sm text-slate-500">No divisions have been attached to Engineering Department yet.</p>
          )}
          {outsideDivisions.length > 0 && (
            <div className="mt-6 border-t border-amber-200 pt-4">
              <p className="text-xs font-semibold text-amber-800 mb-3">Members not assigned to a division</p>
              <div className="flex flex-wrap gap-3">
                {outsideDivisions.map(person => <PersonCard key={person.id} person={person} size="sm" unresolved />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default function OrgChart() {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useQuery<OrgChartData>({
    queryKey: ['org-chart'], queryFn: () => api.get('/teams/org-chart').then(response => response.data), staleTime: 30000,
  })
  if (isLoading) return <div className="space-y-4"><div className="h-8 w-56 bg-slate-200 rounded animate-pulse" /><div className="h-48 bg-slate-100 rounded-2xl animate-pulse" /></div>
  if (isError) return <div className="text-center text-sm text-slate-500 py-12">Failed to load organisation chart.</div>
  const { leaders = [], teams = [], unassigned = [] } = data || {}
  const engineering = teams.find(team => (team.divisions?.length || 0) > 0
    || team.name.toLowerCase() === 'engineering department')
  const visibleLeaders = leaders.filter(person => !(engineering
    && (person.role === 'chief_engineer' || person.id === engineering.manager_id)))
  const otherTeams = teams.filter(team => team.id !== engineering?.id
    && team.department_name?.toLowerCase() !== 'engineering department')
  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-center gap-2 text-xs font-semibold text-red-800 uppercase tracking-wider"><Network size={14} /> Reporting structure</div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mt-2">Organisation Chart</h1>
        <p className="text-sm text-slate-500 mt-1">Department Heads sit at the root. Every branch follows its member’s direct supervisor.</p>
      </header>
      {visibleLeaders.length > 0 && (
        <section className="bg-slate-800 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-300 uppercase tracking-wider mb-4"><UsersRound size={14} /> Leadership</div>
          <div className="flex flex-wrap gap-4">{visibleLeaders.map(person => <PersonCard key={person.id} person={person} />)}</div>
        </section>
      )}
      {(visibleLeaders.length > 0 || engineering) && teams.length > 0 && <div className="flex justify-center"><ArrowDown size={18} className="text-slate-300" /></div>}
      {engineering && <EngineeringHierarchy team={engineering} />}
      {otherTeams.length > 0 ? otherTeams.map(team => <DepartmentHierarchy key={team.id} team={team} />) : (
        !engineering && teams.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-400">No departments have been created yet. Create departments in User Management → Teams.</div>
        ) : null
      )}
      {unassigned.length > 0 && (
        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div><h2 className="text-xs font-bold text-amber-800 uppercase tracking-wider">Outside departments · {unassigned.length}</h2><p className="text-xs text-amber-700 mt-1">These active users are not members of a department.</p></div>
            <button onClick={() => navigate('/users?tab=teams')} className="text-xs px-3 py-1.5 bg-red-800 text-white rounded-lg hover:bg-red-900 font-medium">Manage departments</button>
          </div>
          <div className="flex flex-wrap gap-3">{unassigned.map(person => <PersonCard key={person.id} person={person} size="sm" unresolved />)}</div>
        </section>
      )}
    </div>
  )
}